import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useToast } from "@shared/hooks/use-toast";
import {
  Lightbulb,
  Droplet,
  Flame,
  ThumbsUp,
  Bookmark,
  Share2,
  Zap,
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  HelpCircle,
  Settings,
  Shield,
  Star,
  Target,
  Users,
} from "lucide-react";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useTranslation } from "react-i18next";
import { useTipsData, useUpdateTipsStatus } from "../../hooks";
import { useService } from "@features/dashboard/hooks";
import { logEvent } from "@shared/analytics/analytics";

// Payload interface for the helpful mutation
interface AddHelpfullPayload {
  code: string;
  remote_utility_id: string;
  is_helpful: number;
}

// Separate component for tips grid to avoid hook violations
const TipsGrid = React.memo(
  ({
    tips,
    onTipSelect,
    getTipIcon,
    t,
  }: {
    tips: any[];
    onTipSelect: (tip: any) => void;
    getTipIcon: (iconName: string) => React.ReactNode;
    t: any;
  }) => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {tips.map((tip) => (
        <Card key={tip.code} className="flex flex-col h-64 overflow-hidden">
          <CardHeader className="pb-3 text-center flex-shrink-0">
            <div className="flex justify-center mb-3">
              {tip.icon ? (
                <div className="text-6xl">{getTipIcon(tip.icon)}</div>
              ) : (
                <Info className="text-6xl text-gray-500" />
              )}
            </div>
            <CardTitle className="text-lg font-semibold leading-tight">
              {tip.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col px-4 pb-0">
            <p className="text-sm text-muted-foreground line-clamp-4 flex-1">
              {tip.quickSummary || tip.description}
            </p>
          </CardContent>
          <CardFooter className="pt-4 pb-4 px-4 flex-shrink-0">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onTipSelect(tip)}
            >
              {t("Learn More")}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  )
);

const EfficiencyTips = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedTip, setSelectedTip] = useState<any>(null);
  const [selectedUtility, setSelectedUtility] = useState<string>("All");
  const [helpfulTips, setHelpfulTips] = useState<Set<string>>(new Set());
  const [debugCounter, setDebugCounter] = useState(0); // Force re-render counter

  // Create ref for the Quick Summary section
  const quickSummaryRef = useRef<HTMLDivElement>(null);

  // Get utility services from API
  const { remoteUtilityId, remoteConsumerNumber, consumerId } =
    getLoginDataFromStorage();
  const { data: serviceData, isLoading: servicesLoading } = useService({
    consumer: consumerId,
  });

  // Helpful mutation hook
  const { mutate: updateTipsStatus, isPending: isUpdatingHelpful } =
    useUpdateTipsStatus();

  // Filter active utility services
  const activeServices = useMemo(() => {
    if (!serviceData?.result) return [];
    return serviceData.result;
  }, [serviceData]);

  useEffect(() => {
    logEvent("Tips Viewed");
  }, []);

  const {
    data: tipsData,
    isLoading: tipsLoading,
    error: tipsError,
  } = useTipsData({
    remote_utility_id: remoteUtilityId,
    utility_service: selectedUtility === "All" ? "" : selectedUtility,
    show_inactive: true,
  });

  // Helper functions
  const getServiceIcon = useCallback((serviceName: string) => {
    const name = serviceName.toLowerCase();
    if (name === "electricity")
      return <Lightbulb className="h-4 w-4 text-yellow-500" />;
    if (name.includes("water"))
      return <Droplet className="h-4 w-4 text-blue-500" />;
    if (name === "gas") return <Flame className="h-4 w-4 text-orange-500" />;
    return <Zap className="h-4 w-4 text-gray-500" />;
  }, []);

  const getTipIcon = useCallback((iconName: string) => {
    const name = iconName.toLowerCase();
    switch (name) {
      case "bulb":
      case "lightbulb":
        return <Lightbulb className="text-6xl text-yellow-500" />;
      case "info":
        return <Info className="text-6xl text-gray-500" />;
      case "alert-triangle":
        return <AlertTriangle className="text-6xl text-red-500" />;
      case "check-circle":
        return <CheckCircle className="text-6xl text-green-500" />;
      case "help-circle":
        return <HelpCircle className="text-6xl text-blue-400" />;
      case "star":
        return <Star className="text-6xl text-yellow-400" />;
      case "zap":
      case "electric":
        return <Zap className="text-6xl text-purple-500" />;
      case "target":
        return <Target className="text-6xl text-pink-500" />;
      case "shield":
        return <Shield className="text-6xl text-slate-600" />;
      case "clock":
        return <Clock className="text-6xl text-teal-600" />;
      case "users":
        return <Users className="text-6xl text-orange-600" />;
      case "settings":
        return <Settings className="text-6xl text-gray-600" />;
      case "file-text":
        return <FileText className="text-6xl text-indigo-500" />;
      case "droplet":
      case "water":
        return <Droplet className="text-6xl text-blue-500" />;
      case "flame":
      case "fire":
      case "gas":
        return <Flame className="text-6xl text-orange-500" />;
      default:
        return <Info className="text-6xl text-gray-500" />;
    }
  }, []);

  // Get ALL tips for current utility
  const allTips = useMemo(() => {
    if (!tipsData?.result) return [];
    return tipsData.result;
  }, [tipsData]);

  // Enhanced tip selection handler
  const handleTipSelect = useCallback((tip: any) => {
    setSelectedTip(tip);
  }, []);

  // Handle helpful button click
  const handleHelpfulClick = useCallback(
    (tip: any) => {
      if (helpfulTips.has(tip.code) || isUpdatingHelpful) return;

      const payload: AddHelpfullPayload = {
        code: tip.code,
        remote_utility_id: remoteUtilityId,
        is_helpful: 1,
      };

      updateTipsStatus(payload, {
        onSuccess: () => {
          setHelpfulTips((prev) => new Set([...prev, tip.code]));
          toast({
            title: t("Thank you!"),
            description: t("Your feedback for \"{{name}}\" has been recorded.", { name: tip.name }),
          });
        },
        onError: () => {
          toast({
            title: t("Error"),
            description: t("Failed to record your feedback. Please try again."),
            variant: "destructive",
          });
        },
      });
    },
    [helpfulTips, isUpdatingHelpful, remoteUtilityId, updateTipsStatus, toast]
  );

  // Handle URL parameters
  useEffect(() => {
    const tipId = searchParams.get("tipId");
    if (tipId) {
      const tip = allTips.find((t) => t.code === tipId);
      if (tip) {
        handleTipSelect(tip);
      }
    }
  }, [searchParams, allTips, handleTipSelect]);

  const handleSaveTip = useCallback(
    (tip: any) => {
      toast({
        title: t("Tip Saved"),
        description: t("\"{{name}}\" has been saved to your collection.", { name: tip.name }),
      });
    },
    [toast, t]
  );

  const handleShareTip = useCallback(
    (tip: any) => {
      toast({
        title: t("Sharing Tip"),
        description: t("Share option for \"{{name}}\" would open here.", { name: tip.name }),
      });
    },
    [toast, t]
  );

  const serviceCount = activeServices.length;
  const isCurrentTipHelpful = selectedTip && helpfulTips.has(selectedTip.code);

  // Don't render if no services
  if (serviceCount === 0 && !servicesLoading) {
    return <div>{t("No services found.")}</div>;
  }

  return (
    <div className="space-y-6">
      {!selectedTip ? (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <Select value={selectedUtility} onValueChange={setSelectedUtility}>
              <SelectTrigger className="w-[200px] h-10 bg-white border-slate-200">
                <SelectValue placeholder={t("Filter by Service")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">{t("All Services")}</SelectItem>
                {activeServices.map((service: string) => (
                  <SelectItem key={service} value={service}>{service}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tipsLoading ? (
            <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
          ) : allTips.length > 0 ? (
            <TipsGrid
              tips={allTips}
              onTipSelect={handleTipSelect}
              getTipIcon={getTipIcon}
              t={t}
            />
          ) : (
            <div className="text-center p-8 text-slate-500">{t("No tips found for the selected service.")}</div>
          )}
        </div>
      ) : (
        <Card className="shadow-sm border border-slate-100 rounded-xl bg-white overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="max-w-[100%]">
                <CardTitle className="text-[22px] font-semibold text-slate-900 tracking-tight">
                  {selectedTip.name}
                </CardTitle>
              </div>
              {selectedTip.icon && (
                <div className="shrink-0 ml-4 flex items-center justify-center">
                  {!selectedTip.icon.match(/^[a-zA-Z-]+$/) ? (
                    selectedTip.icon.startsWith("http") ? (
                      <img src={selectedTip.icon} alt="icon" className="w-8 h-8 object-contain" />
                    ) : (
                      <span className="text-3xl">{selectedTip.icon}</span>
                    )
                  ) : (
                    <div className="[&>svg]:w-8 [&>svg]:h-8">
                      {getTipIcon(selectedTip.icon)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className=" space-y-5">
            <div ref={quickSummaryRef}>
              <p className="font-medium text-[15px] text-slate-800 mb-0.5">{t("Quick Summary")}</p>
              <p className="text-blue-500 text-[15px]">
                {selectedTip.quickSummary}
              </p>
            </div>

            <div>
              <p className="font-medium text-[15px] text-slate-800 mb-0.5">{t("Detailed Information")}</p>
              <p className="text-blue-500 text-[15px] leading-relaxed">
                {selectedTip.description}
              </p>
            </div>
          </CardContent>

          <div className="px-6 pb-6 pt-2">
            <CardFooter className="flex justify-between items-center p-0 pt-6 border-t border-slate-100">
              <Button variant="outline" className="h-10 px-5 font-medium border-slate-200" onClick={() => setSelectedTip(null)}>
                {t("Back to Tips")}
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" size="icon" className="h-10 w-10 text-slate-500 border-slate-200">
                  <Bookmark className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-10 w-10 text-slate-500 border-slate-200">
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  size="default"
                  disabled={isCurrentTipHelpful || isUpdatingHelpful}
                  onClick={() => handleHelpfulClick(selectedTip)}
                  variant={isCurrentTipHelpful ? "default" : "default"}
                  className={`h-10 px-5 font-medium ${
                    isCurrentTipHelpful ? "bg-green-600 hover:bg-green-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isUpdatingHelpful ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ThumbsUp className="h-4 w-4 mr-2" />
                  )}
                  {isCurrentTipHelpful ? t("Marked Helpful") : t("Helpful")}
                </Button>
              </div>
            </CardFooter>
          </div>
        </Card>
      )}
    </div>
  );
};

export default EfficiencyTips;
